import { TIMER_STATE, type TimerState } from '../config/constants';

export type { TimerState };

/**
 * Wall-clock countdown timer with pause/resume.
 * Uses performance.now() so it survives tab throttling (we explicitly pause on XR blur via Q17).
 */
export class SessionTimer {
  private state: TimerState = TIMER_STATE.idle;
  private durationMs = 0;
  private startedAtMs = 0;
  private accumulatedMs = 0;

  start(durationMs: number, nowMs: number): void {
    this.durationMs = durationMs;
    this.startedAtMs = nowMs;
    this.accumulatedMs = 0;
    this.state = TIMER_STATE.running;
  }

  pause(nowMs: number): void {
    if (this.state !== TIMER_STATE.running) return;
    this.accumulatedMs += nowMs - this.startedAtMs;
    this.state = TIMER_STATE.paused;
  }

  resume(nowMs: number): void {
    if (this.state !== TIMER_STATE.paused) return;
    this.startedAtMs = nowMs;
    this.state = TIMER_STATE.running;
  }

  end(nowMs: number): void {
    if (this.state === TIMER_STATE.running) {
      this.accumulatedMs += nowMs - this.startedAtMs;
    }
    this.state = TIMER_STATE.ended;
  }

  getState(): TimerState {
    return this.state;
  }

  /** Remaining milliseconds, clamped to [0, durationMs]. */
  remainingMs(nowMs: number): number {
    const elapsed = this.elapsedMs(nowMs);
    return Math.max(0, this.durationMs - elapsed);
  }

  elapsedMs(nowMs: number): number {
    if (this.state === TIMER_STATE.idle) return 0;
    if (this.state === TIMER_STATE.paused || this.state === TIMER_STATE.ended) {
      return this.accumulatedMs;
    }
    return this.accumulatedMs + (nowMs - this.startedAtMs);
  }

  isExpired(nowMs: number): boolean {
    return this.state === TIMER_STATE.running && this.elapsedMs(nowMs) >= this.durationMs;
  }
}

/** Format a millisecond duration as `mm:ss` (clamped at zero). Shared HUD timer label. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}
